import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartPanel from "./ChartPanel";
import KpiCard from "./KpiCard";
import { colors } from "../utils/datosTheme";
import { formatMonthLabel, formatNumber } from "../utils/datosFormatters";

function MetricToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, next) => next && onChange(next)}
      sx={{
        "& .MuiToggleButton-root": {
          minWidth: 132,
          height: 38,
          px: 1.6,
          py: 0,
          color: colors.blue,
          borderColor: colors.line,
          backgroundColor: "rgba(255,255,255,0.88)",
          fontSize: 13,
          fontWeight: 850,
          lineHeight: 1.2,
          textTransform: "none",
          "&.Mui-selected": {
            bgcolor: colors.blue,
            color: "#fff",
            borderColor: colors.blue,
            boxShadow: "none",
            "&:hover": { bgcolor: "#004be0", borderColor: "#004be0" },
          },
        },
      }}
    >
      <ToggleButton value="clientes" aria-label="clientes-nuevos">Clientes nuevos</ToggleButton>
      <ToggleButton value="consultas" aria-label="consultas-nuevas">Consultas nuevas</ToggleButton>
    </ToggleButtonGroup>
  );
}

function MetricChartTitle({ metric, onMetricChange, suffix }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <MetricToggle value={metric} onChange={onMetricChange} />
      <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15 }}>
        {suffix}
      </Typography>
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const tableHeadSx = {
  color: colors.ink,
  fontWeight: 950,
  fontSize: 12,
  bgcolor: "#f7f7f9",
  borderColor: colors.line,
  whiteSpace: "nowrap",
};

const tableCellSx = {
  color: colors.ink,
  fontSize: 12,
  borderColor: colors.line,
  whiteSpace: "nowrap",
};

export default function ClientesDashboard({ data }) {
  const [monthlyMetric, setMonthlyMetric] = useState("consultas");
  const kpis = data?.kpis || {};
  const monthly = useMemo(
    () => (data?.mensual || []).map((item) => ({ ...item, label: formatMonthLabel(item.mes) })),
    [data]
  );
  const origenes = data?.origenes || [];
  const rendimiento = data?.rendimiento_origen || [];
  const consultasRanking = data?.consultas_ranking || [];

  const hasMonthlyData = monthly.some((item) => Number(item.clientes_nuevos || 0) > 0 || Number(item.consultas_nuevas || 0) > 0);
  const hasOrigins = origenes.some((item) => Number(item.total || 0) > 0);
  const hasConversionOrigin = rendimiento.some((item) => Number(item.conversion_pct || 0) > 0);
  const hasConsultasRanking = consultasRanking.length > 0;

  const kpiDescriptions = {
    consultas_nuevas: "Leads iniciados en el período",
    clientes_nuevos: "Clientes registrados en el período",
    consultas_promedio_por_cliente: "Consultas del período dividido por todos los clientes",
    leads_sin_asignar: "Consultas activas sin vendedor asignado",
    clientes_convertidos: "Leads con venta en el período",
    clientes_baja: "Leads cerrados como baja en el período",
    clientes_futuro: "Leads cerrados o programados como futuro",
    tiempo_promedio_cierre_dias: "Promedio hasta venta, baja o futuro",
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          "consultas_nuevas",
          "clientes_nuevos",
          "consultas_promedio_por_cliente",
          "leads_sin_asignar",
          "clientes_convertidos",
          "clientes_baja",
          "clientes_futuro",
          "tiempo_promedio_cierre_dias",
        ].map((id) => (
          <KpiCard
            key={id}
            id={id}
            value={kpis[id]}
            description={kpiDescriptions[id]}
          />
        ))}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel
          title={<MetricChartTitle metric={monthlyMetric} onMetricChange={setMonthlyMetric} suffix="por mes" />}
          empty={!hasMonthlyData}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
              {monthlyMetric === "clientes" ? (
                <Bar dataKey="clientes_nuevos" name="Clientes nuevos" fill={colors.blue} radius={[5, 5, 0, 0]} />
              ) : (
                <Bar dataKey="consultas_nuevas" name="Consultas nuevas" fill={colors.green} radius={[5, 5, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            border: `1px solid ${colors.line}`,
            borderRadius: "8px",
            backgroundColor: colors.panel,
            boxShadow: "0 10px 24px rgba(15, 42, 89, 0.07)",
            p: { xs: 1.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15, mb: 0.5 }}>
            Clientes con más consultas
          </Typography>
          <Typography sx={{ color: colors.muted, fontWeight: 650, fontSize: 11.5, mb: 1.5 }}>
            Top 10 por cantidad de consultas en el período seleccionado.
          </Typography>
          {hasConsultasRanking ? (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Cliente", "Origen", "Contacto", "Última consulta", "Consultas"].map((head) => (
                      <TableCell
                        key={head}
                        align={head === "Consultas" ? "right" : "left"}
                        sx={tableHeadSx}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultasRanking.map((row) => (
                    <TableRow key={row.cliente_id}>
                      <TableCell sx={{ ...tableCellSx, fontWeight: 850 }}>
                        {row.cliente_nombre || "-"}
                      </TableCell>
                      <TableCell sx={tableCellSx}>{row.origen || "Sin origen"}</TableCell>
                      <TableCell sx={tableCellSx}>{row.telefono || row.email || "-"}</TableCell>
                      <TableCell sx={tableCellSx}>{formatDateTime(row.ultima_consulta)}</TableCell>
                      <TableCell align="right" sx={{ ...tableCellSx, fontWeight: 900 }}>{formatNumber(row.consultas)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                minHeight: 86,
                display: "grid",
                placeItems: "center",
                border: `1px dashed ${colors.line}`,
                borderRadius: "8px",
                color: colors.muted,
                bgcolor: "rgba(246,248,252,0.65)",
              }}
            >
              <Typography sx={{ fontWeight: 850, fontSize: 13 }}>
                Sin consultas en el período
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <ChartPanel title="Leads por origen" empty={!hasOrigins}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={origenes} layout="vertical" margin={{ top: 6, right: 18, left: 34, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={128} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Bar dataKey="total" name="Leads" fill={colors.blue} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Conversión por origen" empty={!hasConversionOrigin}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rendimiento} layout="vertical" margin={{ top: 6, right: 18, left: 34, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} tickFormatter={(value) => `${value}%`} />
              <YAxis type="category" dataKey="origen" width={128} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`} />
              <Bar dataKey="conversion_pct" name="Conversión" fill={colors.green} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            border: `1px solid ${colors.line}`,
            borderRadius: "8px",
            backgroundColor: colors.panel,
            boxShadow: "0 10px 24px rgba(15, 42, 89, 0.07)",
            p: { xs: 1.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15, mb: 1.5 }}>
            Rendimiento por origen
          </Typography>
          <TableContainer sx={{ maxHeight: 340 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Origen", "Clientes", "Consultas", "Ventas", "Bajas", "Futuros", "Conversión", "Cierre"].map((head) => (
                    <TableCell
                      key={head}
                      align={head === "Origen" ? "left" : "right"}
                      sx={{
                        color: colors.ink,
                        fontWeight: 950,
                        fontSize: 12,
                        bgcolor: "#f7f7f9",
                        borderColor: colors.line,
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rendimiento.map((row) => (
                  <TableRow key={row.origen}>
                    <TableCell sx={{ color: colors.ink, fontWeight: 850, fontSize: 12, borderColor: colors.line }}>
                      {row.origen}
                    </TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{formatNumber(row.clientes)}</TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{formatNumber(row.consultas)}</TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{formatNumber(row.ventas)}</TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{formatNumber(row.bajas)}</TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{formatNumber(row.futuros)}</TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{Number(row.conversion_pct || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%</TableCell>
                    <TableCell align="right" sx={{ color: colors.ink, fontSize: 12, borderColor: colors.line }}>{Number(row.tiempo_promedio_cierre_dias || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })}d</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
}
